import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useLanguage();
  
  const slides = [
    {
      title: t('hero.tattooSlide.title'),
      description: t('hero.tattooSlide.description'),
      imageSrc: "/images/tattoback.png",
      path: "/tattoo-design"
    },
    {
      title: t('hero.nailSlide.title'),
      description: t('hero.nailSlide.description'),
      imageSrc: "/images/nails1.png",
      path: "/nail-design"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <section className="relative min-h-screen">
      {/* Background image with overlay */}
      <div className="absolute inset-0 overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ 
                backgroundImage: `url(${slide.imageSrc})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-70" />
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 min-h-screen flex items-center relative z-10">
        <div className="w-full max-w-3xl">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ease-in-out transform ${
                currentSlide === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 absolute'
              }`}
              style={{ display: currentSlide === index ? 'block' : 'none' }}
            >
              {currentSlide === index && (
                <>
                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                      {slide.title}
                    </span>
                  </h1>
                  <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-6 md:mb-8 max-w-2xl">
                    {slide.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to={slide.path}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 rounded-full font-medium flex items-center justify-center sm:justify-start gap-2 transition-all"
                    >
                      {t('hero.tryNow')} <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      to="/gallery"
                      className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3 rounded-full font-medium flex items-center justify-center sm:justify-start gap-2 transition-all border border-gray-700"
                    >
                      {t('hero.viewGallery')}
                    </Link>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              currentSlide === index ? 'bg-pink-500 w-8' : 'bg-gray-400 bg-opacity-50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;